/**
 * MANUALLY MAINTAINED — mirrors App\Enums\Commerce\ItemStatus.
 * Last synced: 2026-09-02 (AWAITING_APPROVAL retired as a written state; value
 * reserved — see ../../../../backend/app/Enums/Commerce/ItemStatus.php).
 *
 * This file previously claimed to be auto-generated by `npm run generate:enums`.
 * That was false three ways and it let the file rot for months: there is no such
 * npm script; the backend's `enums:publish` command does not know ItemStatus
 * exists; and it writes into the backend repo, never into this one. Nobody
 * "regenerates" this — you edit it by hand when the PHP enum changes.
 *
 * Keep the VALUES in step with ../backend/app/Enums/Commerce/ItemStatus.php.
 * Note the two irregulars carried over from the pre-unification schema:
 * SHIPPED = 5 and CANCELLED = 7 break the decade banding of every other case.
 *
 * DISPLAY METADATA, TRANSITIONS AND CATEGORIES LIVE IN ItemStatusConfig.js —
 * not here. This file is the numeric enum and nothing else. The duplicate
 * `ItemStatusCategories` that used to sit at the bottom was deleted 2026-08-09:
 * it had drifted (EDITABLE = [PENDING] against the live [PENDING, COLLECTING,
 * CANCELLED]) so the same item gave two different answers depending on which
 * object you asked. `ItemStatusMeta` and `ItemStatusTransitions` below are the
 * remaining dead duplicates — zero importers, an `itemStatus.*` i18n prefix that
 * nothing renders, and non-Framework7 colour names. MARKED FOR RETIREMENT in a
 * dedicated cleanup pass (repo rule: no inline deletions of existing surface).
 */

export const ItemStatus = {
  // Initial state
  PENDING: 1,

  // Collection workflow
  COLLECTING: 11,
  AWAITING_REVIEW: 12,

  // Post-collection states
  COLLECTED: 21,
  PACKAGED: 22,
  SHIPPED: 5,
  DELIVERED: 24,

  // Issue states
  ISSUE: 31,
  AWAITING_APPROVAL: 32,

  // Terminal states
  OUT_OF_STOCK: 41,
  CANCELLED: 7, // Legacy value maintained
  // Item lost, damaged, or returned unfit AFTER it was collected. Terminal, and
  // distinct from OUT_OF_STOCK (which is the supplier never having it). The
  // money path depends on the parent box: post-delivery-confirm it is a real
  // cash refund (RefundWrittenOffItemAction), pre-confirm the hold is released
  // by rebuildInvoice, and a Poola line is a documented no-op. Either way the
  // member is not billed for it — see nonActiveCases() in the PHP enum.
  WRITTEN_OFF: 60,
};

/**
 * DEAD DUPLICATE — zero importers; the live metadata is ItemStatusMeta in
 * ItemStatusConfig.js. Its `itemStatus.*` i18n prefix and its non-Framework7
 * colour names (indigo / emerald / amber / slate) are rendered by nothing.
 * Marked for retirement; do not add to it — WRITTEN_OFF is deliberately absent.
 */
export const ItemStatusMeta = {
  [ItemStatus.PENDING]: {
    i18nKey: 'itemStatus.pending',
    color: 'gray',
    icon: 'clock',
  },

  [ItemStatus.COLLECTING]: {
    i18nKey: 'itemStatus.collecting',
    color: 'blue',
    icon: 'arrow_clockwise',
  },

  [ItemStatus.AWAITING_REVIEW]: {
    i18nKey: 'itemStatus.awaitingReview',
    color: 'yellow',
    icon: 'eye',
  },

  [ItemStatus.COLLECTED]: {
    i18nKey: 'itemStatus.collected',
    color: 'green',
    icon: 'checkmark_circle',
  },

  [ItemStatus.PACKAGED]: {
    i18nKey: 'itemStatus.packaged',
    color: 'purple',
    icon: 'cube_box',
  },

  [ItemStatus.SHIPPED]: {
    i18nKey: 'itemStatus.shipped',
    color: 'indigo',
    icon: 'airplane',
  },

  [ItemStatus.DELIVERED]: {
    i18nKey: 'itemStatus.delivered',
    color: 'emerald',
    icon: 'checkmark_seal',
  },

  [ItemStatus.ISSUE]: {
    i18nKey: 'itemStatus.issue',
    color: 'orange',
    icon: 'exclamationmark_triangle',
  },

  [ItemStatus.AWAITING_APPROVAL]: {
    i18nKey: 'itemStatus.awaitingApproval',
    color: 'amber',
    icon: 'hand_raised',
  },

  [ItemStatus.OUT_OF_STOCK]: {
    i18nKey: 'itemStatus.outOfStock',
    color: 'red',
    icon: 'xmark_octagon',
  },

  [ItemStatus.CANCELLED]: {
    i18nKey: 'itemStatus.cancelled',
    color: 'slate',
    icon: 'xmark_circle',
  },
};

/**
 * DEAD DUPLICATE — zero importers; the live table is ItemStatusTransitions in
 * ItemStatusConfig.js. Marked for retirement alongside ItemStatusMeta above.
 */
export const ItemStatusTransitions = {
  [ItemStatus.PENDING]: [
    ItemStatus.COLLECTING,
    ItemStatus.CANCELLED,
  ],

  [ItemStatus.COLLECTING]: [
    ItemStatus.AWAITING_REVIEW,
    ItemStatus.ISSUE,
    ItemStatus.AWAITING_APPROVAL,
    ItemStatus.OUT_OF_STOCK,
  ],

  [ItemStatus.AWAITING_REVIEW]: [
    ItemStatus.COLLECTED,
    ItemStatus.OUT_OF_STOCK,
    ItemStatus.PENDING,
    ItemStatus.ISSUE,
  ],

  [ItemStatus.AWAITING_APPROVAL]: [
    ItemStatus.COLLECTING,
    ItemStatus.OUT_OF_STOCK,
    ItemStatus.CANCELLED,
  ],

  [ItemStatus.ISSUE]: [
    ItemStatus.COLLECTING,
    ItemStatus.AWAITING_APPROVAL,
    ItemStatus.OUT_OF_STOCK,
    ItemStatus.CANCELLED,
  ],

  [ItemStatus.COLLECTED]: [
    ItemStatus.PACKAGED,
    ItemStatus.CANCELLED,
  ],

  [ItemStatus.PACKAGED]: [
    ItemStatus.SHIPPED,
    ItemStatus.COLLECTED,
  ],

  [ItemStatus.SHIPPED]: [
    ItemStatus.DELIVERED,
  ],

  [ItemStatus.CANCELLED]: [
    ItemStatus.PENDING, // Restore to pending
  ],

  // Terminal states (no transitions except OUT_OF_STOCK might allow manual override)
  [ItemStatus.DELIVERED]: [],
  [ItemStatus.OUT_OF_STOCK]: [],
};

// ItemStatusCategories used to be exported from here as well. It was DELETED on
// 2026-08-09 because two objects of the same name gave the same item two
// different answers: this copy's EDITABLE was [PENDING] while the live one was
// [PENDING, COLLECTING, CANCELLED], and OrderBoxItem.js imported BOTH. Its
// NEEDS_ATTENTION had drifted too, and it never gained QUANTITY_EDITABLE,
// BLOCKS_SHIPPING or RESOLUTION_ACTIONABLE.
//
// The single authoritative object is ItemStatusCategories in ItemStatusConfig.js.
// Import it from there. Do not re-add a copy here.
